import { useCallback, useState } from 'react';

const STORAGE_PREFIX = 'civic-alert:panel';

export function panelStorageKey(scope: string, id: string): string {
  return `${STORAGE_PREFIX}:${scope}:${id}`;
}

function readStoredExpanded(scope: string, id: string, fallback: boolean): boolean {
  if (typeof window === 'undefined') return fallback;
  try {
    const raw = window.localStorage.getItem(panelStorageKey(scope, id));
    if (raw === 'true') return true;
    if (raw === 'false') return false;
  } catch {
    /* ignore quota / private mode */
  }
  return fallback;
}

function writeStoredExpanded(scope: string, id: string, expanded: boolean): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(panelStorageKey(scope, id), String(expanded));
  } catch {
    /* ignore */
  }
}

/** Persist collapsible panel open/closed state in localStorage. */
export function useCollapsiblePanelState(
  scope: string,
  id: string,
  defaultExpanded: boolean,
): [boolean, (next: boolean | ((prev: boolean) => boolean)) => void] {
  const [expanded, setExpandedState] = useState(() =>
    readStoredExpanded(scope, id, defaultExpanded),
  );

  const setExpanded = useCallback(
    (next: boolean | ((prev: boolean) => boolean)) => {
      setExpandedState((prev) => {
        const value = typeof next === 'function' ? next(prev) : next;
        writeStoredExpanded(scope, id, value);
        return value;
      });
    },
    [scope, id],
  );

  return [expanded, setExpanded];
}
